import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { protectedResources } from '../auth/auth.config';
import {
  ConfirmationRequest,
  DeviceAssociationInfo,
  DeviceAssociationRequest,
  DeviceAuthorizationResponse,
  DeviceCertificateApprovalBody,
  DeviceCertificateInfo,
  DeviceRegistrationRequest,
  DeviceRegistrationResponse,
  SearchResultsDeviceAssociationInfo,
} from '../models/certificate.models';
import {
  CreateDeviceFromTemplateRequest,
  DeviceDto,
  DeviceTemplateListDto,
} from '../models/device.model';

@Injectable({
  providedIn: 'root',
})
export class CertificateService {
  private apiEndpoint: string = protectedResources.apiTodoList.endpoint;

  constructor(private http: HttpClient) {}

  getAssociationByConfirmationPin(
    confirmationPin: string,
  ): Observable<DeviceAssociationInfo> {
    const encodedPinNumber = encodeURIComponent(confirmationPin);
    return this.http.get<DeviceAssociationInfo>(
      `${this.apiEndpoint}/device/identities/registration/confirmation?confirmationPin=${encodedPinNumber}`,
    );
  }
  searchAssociationByDeviceIdSubstring(
    deviceIdSubString: string,
    limit: number = 5,
  ): Observable<SearchResultsDeviceAssociationInfo> {
    const encodedSubString = encodeURIComponent(deviceIdSubString.trim());
    return this.http.get<SearchResultsDeviceAssociationInfo>(
      `${this.apiEndpoint}/device/identities/registration/requests?deviceIdSubstring=${encodedSubString}&limit=${limit}`,
    );
  }

  getDeviceCertificateInfoByPasscode(
    pinNumber: string,
  ): Observable<DeviceCertificateInfo> {
    return this.getAssociationByConfirmationPin(pinNumber);
  }

  confirmAssociation(
    confirmationPin: string,
    body: DeviceAssociationRequest,
  ): Observable<void> {
    const encodedPinNumber = encodeURIComponent(confirmationPin);
    const requestBody: DeviceAssociationRequest = {
      deviceTypeOverride: body.deviceTypeOverride,
      deviceIdOverride: body.deviceIdOverride,
      companyId: body.companyId,
      nameAlias: body.nameAlias,
      note: body.note,
    };
    return this.http.post<void>(
      `${this.apiEndpoint}/device/identities/registration/confirmation?confirmationPin=${encodedPinNumber}`,
      requestBody,
    );
  }

  validateDeviceCertificate(
    confirmationPin: string,
    body: DeviceCertificateApprovalBody,
  ): Observable<void> {
    return this.confirmAssociation(confirmationPin, body);
  }

  registerDevice(
    body: DeviceRegistrationRequest,
  ): Observable<DeviceRegistrationResponse> {
    return this.http.post<DeviceRegistrationResponse>(
      `${this.apiEndpoint}/device/identities/registration`,
      body,
    );
  }

  listDeviceTemplates(): Observable<DeviceTemplateListDto> {
    return this.http.get<DeviceTemplateListDto>(
      `${this.apiEndpoint}/device/templates`,
    );
  }

  createDeviceFromTemplate(
    templateId: string,
    request: CreateDeviceFromTemplateRequest = {},
  ): Observable<DeviceDto> {
    const encodedTemplateId = encodeURIComponent(templateId);
    return this.http.post<DeviceDto>(
      `${this.apiEndpoint}/device/templates/${encodedTemplateId}`,
      request,
    );
  }

  authorizeDevice(
    body: ConfirmationRequest,
  ): Observable<DeviceAuthorizationResponse> {
    return this.http.post<DeviceAuthorizationResponse>(
      `${this.apiEndpoint}/device/identities/accessToken`,
      body,
    );
  }

  revokeCertificate(thumbprint: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiEndpoint}/device/identities/${encodeURIComponent(thumbprint)}/revoke`,
      null,
    );
  }
}
